import { Result } from '@blade/std-lib'
import { loadScript } from '@root/shared/core/DomUtil'
import { LoadScriptError } from '@root/shared/core/script-loader/script-loader'

declare global {
  interface Window {
    FB?: FacebookSdk
    fbAsyncInit?: () => void
  }
}

export interface FacebookSdk {
  init: (params: { xfbml?: boolean; version?: string }) => void
  XFBML: {
    parse: (node?: HTMLElement, callback?: () => void) => void
  }
}

export interface FacebookXfbml {
  parse: (node?: HTMLElement, callback?: () => void) => void
}

const facebookSdkJs = 'https://connect.facebook.net/en_US/sdk.js'
const facebookSdkVersion = 'v25.0'
const facebookSdkWaitTimeoutMs = 10_000
const facebookSdkPollIntervalMs = 50

let facebookSdkPromise:
  | Promise<Result.Result<FacebookXfbml, LoadScriptError>>
  | undefined
let facebookSdkInitialized = false

const initFacebookSdk = (): Result.Result<FacebookXfbml, LoadScriptError> => {
  if (!window.FB?.XFBML.parse) {
    return Result.fail(
      new LoadScriptError({
        src: facebookSdkJs,
        cause: 'window.FB is undefined',
      }),
    )
  }

  if (!facebookSdkInitialized) {
    window.FB.init({
      xfbml: true,
      version: facebookSdkVersion,
    })

    facebookSdkInitialized = true
  }

  return Result.succeed({
    parse: (node, callback) => window.FB?.XFBML.parse(node, callback),
  })
}

const waitForFacebookSdk = (): Promise<
  Result.Result<FacebookXfbml, LoadScriptError>
> =>
  new Promise((resolve) => {
    const startedAt = Date.now()

    const poll = () => {
      if (window.FB?.XFBML.parse) {
        resolve(initFacebookSdk())

        return
      }

      if (Date.now() - startedAt >= facebookSdkWaitTimeoutMs) {
        resolve(
          Result.fail(
            new LoadScriptError({
              src: facebookSdkJs,
              cause: 'window.FB is undefined',
            }),
          ),
        )

        return
      }

      window.setTimeout(poll, facebookSdkPollIntervalMs)
    }

    poll()
  })

export const loadFacebookSdk = () => {
  if (facebookSdkPromise) {
    return facebookSdkPromise
  }

  facebookSdkPromise = new Promise<
    Result.Result<FacebookXfbml, LoadScriptError>
  >((resolve) => {
    if (window.FB?.XFBML.parse) {
      resolve(initFacebookSdk())

      return
    }

    const previousFbAsyncInit = window.fbAsyncInit

    window.fbAsyncInit = () => {
      previousFbAsyncInit?.()
      resolve(initFacebookSdk())
    }

    loadScript(facebookSdkJs, {
      async: true,
      defer: true,
      attrs: {
        crossorigin: 'anonymous',
      },
    }).then(async (result) => {
      if (Result.isFailure(result)) {
        facebookSdkPromise = undefined
        resolve(Result.fail(result.failure))

        return
      }

      const sdkResult = await waitForFacebookSdk()

      if (Result.isFailure(sdkResult)) {
        facebookSdkPromise = undefined
      }

      resolve(sdkResult)
    })
  })

  return facebookSdkPromise
}
